import React, {useMemo} from "react";
import PropTypes from "prop-types";
import Select from 'react-select';
import styled from "styled-components";
import WindowedSelect, { createFilter } from "react-windowed-select";
import { FormControl, InputLabel } from "@material-ui/core";

const StyledSelect = styled(WindowedSelect)`
	min-width:150px;
	background:transparent
`;


const MySelect = (props) => {
	const [isFocused, setIsFocused] = React.useState(false);
	// Track created options that might not be in props.options yet
	const [createdOptions, setCreatedOptions] = React.useState([]);
	// Track the current text in the input field
	const [inputValue, setInputValue] = React.useState('');
	
	// Combine original options with any created options for value lookup
	const allOptions = React.useMemo(() => {
		const filteredOptions = props.options?.filter(o => !o?._trash) || [];
		return [...filteredOptions, ...createdOptions];
	}, [props.options, createdOptions]);
	
	// For display in the dropdown, add the footer hint if creatable
	const options = React.useMemo(() => {
		const filteredOptions = props.options?.filter(o => !o?._trash) || [];

		return filteredOptions;
	}, [props.options, props.isCreatable]);
	
	// Find value objects from all options (including created ones)
	const value = React.useMemo(() => {
		if (props.value===undefined||props.value===null||props.value==="") return null;
		
		if (props.isMulti) {
			if (!Array.isArray(props.value)) return null;
			
			return props.value.map(v => {
				const option = allOptions.find(d => d.value === v);
				return option || { value: v, label: String(v) };
			});
		} else {
			if (props.value === "") return null;
			const option = allOptions.find(d => d.value === props.value);
			return option || { value: props.value, label: String(props.value) };
		}
	}, [allOptions, props.value, props.isMulti]);
	
	const handleChange = (value) =>  {
		if(!value) {
			props.onChange(null, props.name);
			return;
		}
		
		if(props.isMulti){
			value = value.map(v=>v.value);
		}else{
			value = value.value;
		}
		props.onChange(value, props.name);
	};
	
	const handleCreateOption = (inputValue) => {
		if(!props.isCreatable) return;
		const newOption = { 
			label: inputValue, 
			value: props.formatNewOptionValue ? props.formatNewOptionValue(inputValue) : inputValue
		};
		
		setCreatedOptions(prev => [...prev, newOption]);
		
		if (props.onCreateOption) {
			props.onCreateOption(newOption);
		}
		
		const newValue = props.isMulti 
			? [...(value || []).map(v => v.value), newOption.value]
			: newOption.value;
		
		props.onChange(newValue, props.name);
	};

	// Store the user's input in our state
	const handleInputChange = (currentInput) => {
		setInputValue(currentInput);
	};
	
	// On blur, if the input is not an existing option, create it
	const handleBlur = () => {
		setIsFocused(false);
		if (!inputValue) return; // Do nothing if input is empty

		const existingOption = allOptions.find(opt => opt.label === inputValue);
		
		if (!existingOption) {
			handleCreateOption(inputValue);
		}
	};
	
	const filterConfig = useMemo(() => ({ ignoreAccents: false }), []);

	const hasValue = props.isMulti 
		? (Array.isArray(props.value) && props.value.length > 0)
		: (props.value !== undefined && props.value !== null && props.value !== "");

	const shouldShrink = isFocused || hasValue || !!inputValue;
			
	const selectStyles = { 
		menuPortal: base => ({ ...base, zIndex: 9999 }), 
		control: (base, state) => ({
			...base, 
			background:"transparent", 
			borderRadius:20, 
			border: `1px solid ${state.isFocused ? '#3f51b5' : 'rgba(0, 0, 0, 0.23)'}`,
			padding:"2px 5px", 
			width:"100%", 
			fontFamily: "Avenir",
			boxShadow: state.isFocused ? '0 0 0 1px #3f51b5' : 'none',
			'&:hover': {
				borderColor: state.isFocused ? '#3f51b5' : 'rgba(0, 0, 0, 0.87)',
			},
			...props.style
		}), 
		placeholder: base => ({
			...base, 
			color: "rgba(0, 0, 0, 0.54)",
			fontFamily: "Avenir",
			opacity: shouldShrink ? 1 : 0,
			transition: 'opacity 0.2s',
			...props.placeholderStyle
		}),
		option: (base, state) => {
			let customStyles = {};
			return {
				...base,
				...customStyles
			};
		}
	};
	const selectProps = {
		className: "w-full",
		placeholder: props.label,
		isMulti: props.isMulti,
		options: options,
		onChange: handleChange,
		value: value,
		menuPortalTarget: document.body,
		styles: selectStyles,
		isClearable: props.isClearable,
		formatOptionLabel:props.formatOptionLabel,
		// Conditionally hide the menu
		menuIsOpen: props.isCreatable && inputValue && options?.filter(o=>o.label?.toLowerCase().includes(inputValue?.toLowerCase())).length===0  ? false : undefined,
		// Improve user experience when no options match
		noOptionsMessage: () => 'No options available',
	};
	
	return (
		<FormControl variant="outlined" size="small" fullWidth className="w-full">
			{props.label && (
				<InputLabel 
					shrink={shouldShrink}
					style={{ 
						backgroundColor: 'white', 
						padding: '0 4px', 
						marginLeft: '-4px',
						fontFamily: "Avenir"
					}}
				>
					{props.label}
				</InputLabel>
			)}
			<StyledSelect 
				onFocus={() => setIsFocused(true)}
				onBlur={handleBlur}
				onInputChange={handleInputChange}
				filterOption={createFilter(filterConfig)}
				{...selectProps}
			/>
		</FormControl>
	);
};

MySelect.propTypes = {
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
	options: PropTypes.array,
	name: PropTypes.string,
	onChange: PropTypes.func,
	isMulti: PropTypes.bool,
	isCreatable: PropTypes.bool,
	isClearable: PropTypes.bool,
	onCreateOption: PropTypes.func,
	formatNewOptionValue: PropTypes.func,
	style: PropTypes.object,
	placeholderStyle: PropTypes.object,
	label: PropTypes.string,
};

// Default props
MySelect.defaultProps = {
	isCreatable: false,
	isClearable: true,
	options: []
};

export default MySelect;